import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Radio, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import HeroCard from "@/components/news/HeroCard";
import HeroEditorial from "@/components/news/HeroEditorial";
import HorizontalNewsCard from "@/components/news/HorizontalNewsCard";
import EditorialRow from "@/components/news/EditorialRow";
import NewsCard from "@/components/news/NewsCard";
import SectionHeader from "@/components/news/SectionHeader";
import TodayCarousel from "@/components/news/TodayCarousel";
import { GridSkeleton, HeroSkeleton } from "@/components/news/Skeletons";
import { formatDateShort, formatTime } from "@/lib/date";
import AdSlot from "@/components/ads/AdSlot";
import Sidebar from "@/components/ads/Sidebar";
import MobileAdsStrip from "@/components/ads/MobileAdsStrip";
import { useSEO } from "@/lib/useSEO";

// Device visibility (Tailwind classes) based on block.device_visibility
function visibilityClass(dev) {
  const d = dev || {};
  const parts = [];
  // Base: hidden if not desktop; then reveal per breakpoint
  const desktop = d.desktop !== false;
  const tablet = d.tablet !== false;
  const mobile = d.mobile !== false;
  if (!mobile && !tablet && !desktop) return "hidden";
  parts.push(mobile ? "block" : "hidden");
  parts.push(tablet ? "md:block" : "md:hidden");
  parts.push(desktop ? "lg:block" : "lg:hidden");
  return parts.join(" ");
}

// ---------- Block renderers ----------

function MainBlock({ block }) {
  const items = block.articles || [];
  const hero = items[0];
  if (!hero) return null;

  // A: hero left + 3 right stack (classic, default)
  // B: hero right + 3 left stack (mirror)
  // C: hero on top full-width + 3 below in 3-col grid
  // D: editorial grid (hero big + 3 balanced in a 4x asymmetric grid)
  // E: hero left + 2 right stack + 1 full-width horizontal below
  // F: hero on top + 2 columns below + 1 horizontal below
  const variant = (block.layout_variant || "A").toUpperCase();

  // Fallback if only hero available
  const s = items.slice(1);
  if (s.length === 0) {
    return (
      <section className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
        <HeroEditorial article={hero} mode="overlay" heightClass="min-h-[400px] lg:min-h-[520px]" />
      </section>
    );
  }

  const wrapper = `mb-14 ${visibilityClass(block.device_visibility)}`;

  if (variant === "B") {
    // Hero on the right (imageRight to keep image protagonist), 3 stacked cards on the left
    const three = s.slice(0, 3);
    return (
      <section className={wrapper}>
        <div className="grid grid-cols-1 lg:grid-cols-8 gap-6 lg:items-stretch">
          <div className="lg:col-span-3 flex flex-col gap-4 lg:h-full">
            {three.map((a) => (
              <div key={a.id} className="flex-1 min-h-0">
                <HorizontalNewsCard article={a} imageRight compact />
              </div>
            ))}
          </div>
          <div className="lg:col-span-5">
            <HeroEditorial article={hero} mode="overlay" heightClass="h-full min-h-[420px] lg:min-h-[520px]" />
          </div>
        </div>
      </section>
    );
  }

  if (variant === "C") {
    // Hero on top full-width, 3 secondaries in a 3-column grid below
    const three = s.slice(0, 3);
    return (
      <section className={wrapper}>
        <HeroEditorial article={hero} mode="overlay" heightClass="min-h-[420px] lg:min-h-[520px]" />
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {three.map((a) => (
            <HorizontalNewsCard key={a.id} article={a} compact />
          ))}
        </div>
      </section>
    );
  }

  if (variant === "D") {
    // Editorial grid: hero large (spans 2 cols x 2 rows), 3 balanced tiles
    const three = s.slice(0, 3);
    return (
      <section className={wrapper}>
        <div className="grid grid-cols-1 lg:grid-cols-4 lg:grid-rows-2 gap-5 lg:h-[560px]">
          <div className="lg:col-span-2 lg:row-span-2 lg:h-full">
            <HeroEditorial article={hero} mode="overlay" heightClass="h-full min-h-[420px]" />
          </div>
          {three.map((a) => (
            <div key={a.id} className="lg:col-span-1">
              <HorizontalNewsCard article={a} compact />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (variant === "E") {
    // Hero left + 2 stacked right + 1 full-width horizontal below
    const two = s.slice(0, 2);
    const fourth = s[2];
    return (
      <section className={wrapper}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
          <div className="lg:col-span-2">
            <HeroEditorial article={hero} mode="overlay" heightClass="h-full min-h-[400px] lg:min-h-[480px]" />
          </div>
          <div className="flex flex-col gap-4 lg:h-full">
            {two.map((a) => (
              <div key={a.id} className="flex-1 min-h-0">
                <HorizontalNewsCard article={a} compact />
              </div>
            ))}
          </div>
        </div>
        {fourth && (
          <div className="mt-5">
            <HorizontalNewsCard article={fourth} />
          </div>
        )}
      </section>
    );
  }

  if (variant === "F") {
    // Hero on top + 2 columns (side-by-side) below + 1 horizontal below all
    const two = s.slice(0, 2);
    const fourth = s[2];
    return (
      <section className={wrapper}>
        <HeroEditorial article={hero} mode="overlay" heightClass="min-h-[420px] lg:min-h-[520px]" />
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {two.map((a) => (
            <HorizontalNewsCard key={a.id} article={a} compact />
          ))}
        </div>
        {fourth && (
          <div className="mt-5">
            <HorizontalNewsCard article={fourth} />
          </div>
        )}
      </section>
    );
  }

  // Variant "A" (default) — hero left with overlay + 3 stacked horizontal cards right
  const three = s.slice(0, 3);
  return (
    <section className={wrapper}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
        <div className="lg:col-span-2 lg:h-full">
          <HeroEditorial article={hero} mode="overlay" heightClass="h-full min-h-[420px] lg:min-h-[520px]" />
        </div>
        <div className="flex flex-col gap-3 lg:h-full">
          {three.map((a) => (
            <div key={a.id} className="flex-1 min-h-0">
              <HorizontalNewsCard article={a} compact />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedBlock({ block }) {
  const items = block.articles.slice(0, block.limit || 4);
  if (items.length === 0) return null;
  return (
    <section data-testid={TID.featuredGrid} className={`mb-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 ${visibilityClass(block.device_visibility)}`}>
      {items.map((a) => (
        <NewsCard key={a.id} article={a} variant="compact" showSummary={false} />
      ))}
    </section>
  );
}

function LatestBlock({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  // Editorial rows: 55/45 split, image alternating left/right by index.
  // Title 28–32 px (protagonista), image protagonist without cropping the headline.
  return (
    <section data-testid={TID.latestBlock} className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <SectionHeader title={block.title || "Últimas noticias"} slug="ultimas-noticias" />
      <div className="flex flex-col gap-6">
        {items.map((a, idx) => (
          <EditorialRow key={a.id} article={a} imageRight={idx % 2 === 1} />
        ))}
      </div>
    </section>
  );
}

function GridBlock({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  // Dense grid: up to 4 columns on desktop, 8 items typical (2 rows).
  return (
    <section data-testid={TID.generalGrid} className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <SectionHeader title={block.title || "Panorama general"} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {items.map((a) => (
          <NewsCard key={a.id} article={a} variant="compact" showSummary={false} />
        ))}
      </div>
    </section>
  );
}

function CategoryBlock({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  const tid = block.category_slug === "rio-grande" ? TID.cityBlockRioGrande
    : block.category_slug === "ushuaia" ? TID.cityBlockUshuaia
    : block.category_slug === "tolhuin" ? TID.cityBlockTolhuin
    : block.category_slug === "policiales" ? TID.policeBlock
    : block.category_slug === "deportes" ? TID.sportsBlock
    : `block-${block.category_slug || block.id}`;
  return (
    <section data-testid={tid} className={`mb-14 bg-white border border-softblue p-5 sm:p-6 ${visibilityClass(block.device_visibility)}`}>
      <SectionHeader title={block.title} slug={block.category_slug || undefined} />
      <div className="space-y-4">
        {items[0] && <NewsCard article={items[0]} variant="compact" showSummary={false} />}
        {items.length > 1 && (
          <>
            <div className="divider-hairline" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.slice(1).map((a) => (
                <NewsCard key={a.id} article={a} variant="list" />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function MostReadBlock({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  return (
    <aside data-testid={TID.mostReadBlock} className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <SectionHeader title={block.title || "Más leídas"} accent="turquoise" />
      <ol className="border border-softblue bg-white">
        {items.map((a, i) => (
          <li key={a.id} className="flex gap-4 p-4 border-b border-softblue last:border-b-0 group">
            <span className="font-serif font-black text-3xl leading-none text-watergreen w-8 flex-shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0 flex-1">
              <Link to={`/categoria/${a.main_category_slug}`} className="kicker hover:text-watergreen transition-colors">
                {a.main_category_name}
              </Link>
              <Link to={`/noticia/${a.slug}`}>
                <h3 className="mt-1 font-serif text-base font-bold text-blueblack leading-snug line-clamp-3 group-hover:text-petroleum transition-colors">
                  {a.title}
                </h3>
              </Link>
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}

function VideosBlock({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  return (
    <section data-testid={TID.videosBlock} className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <SectionHeader title={block.title || "Videos"} slug="videos" accent="turquoise" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((a) => (
          <article key={a.id} data-testid={TID.newsCard(a.id)} className="group">
            <Link to={`/noticia/${a.slug}`} data-testid={TID.newsCardLink(a.id)} className="block relative overflow-hidden bg-lightgray aspect-[16/10]">
              <img src={a.main_image} alt={a.title} loading="lazy" className="w-full h-full object-cover card-hover-image" />
              <div className="absolute inset-0 bg-nightblue/25 group-hover:bg-nightblue/10 transition-colors" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center justify-center h-14 w-14 bg-white/95 text-petroleum shadow-lg">
                  <Play className="w-6 h-6 fill-current" />
                </span>
              </span>
            </Link>
            <div className="mt-3">
              <span className="kicker text-turquoise">Video</span>
              <Link to={`/noticia/${a.slug}`}>
                <h3 className="mt-1 font-serif text-base font-bold text-blueblack leading-snug line-clamp-3 group-hover:text-petroleum transition-colors">
                  {a.title}
                </h3>
              </Link>
              <div className="mt-2 text-[11px] uppercase tracking-widest text-textgray">
                {formatDateShort(a.published_at)} · {formatTime(a.published_at)} hs
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function RecommendedBlock({ block }) {
  return <GridBlock block={{ ...block, title: block.title || "Recomendadas" }} />;
}

function BreakingHomeBanner({ block }) {
  const items = block.articles;
  if (items.length === 0) return null;
  return (
    <section className={`mb-8 ${visibilityClass(block.device_visibility)}`}>
      <div className="bg-nightblue text-white border-l-4 border-turquoise px-5 py-4">
        <div className="text-xs uppercase tracking-[0.18em] text-watergreen font-semibold mb-2 flex items-center gap-2">
          <Megaphone className="w-3.5 h-3.5" /> {block.title || "Último momento"}
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {items.map((a) => (
            <li key={a.id}>
              <Link to={`/noticia/${a.slug}`} className="text-sm font-medium hover:underline underline-offset-4 line-clamp-2">
                {a.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LiveBlock({ block }) {
  const embedUrl = block.config?.embed_url;
  return (
    <section className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <div className="border border-softblue bg-nightblue text-white p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-watergreen font-semibold mb-2 flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 animate-pulse" /> {block.title || "En vivo"}
        </div>
        {embedUrl ? (
          <div className="aspect-video bg-black">
            <iframe src={embedUrl} className="w-full h-full" title="Señal en vivo" allow="autoplay; encrypted-media" allowFullScreen />
          </div>
        ) : (
          <p className="text-sm text-white/70">Bloque preparado para la señal en vivo. Configurá la URL desde el panel.</p>
        )}
      </div>
    </section>
  );
}

function AdSlotPlaceholderBlock({ block }) {
  return (
    <section className={`mb-14 ${visibilityClass(block.device_visibility)}`}>
      <div className="border-2 border-dashed border-softblue bg-white/60 text-textgray flex items-center justify-center h-32 sm:h-40 uppercase tracking-widest text-xs font-semibold">
        {block.title || "Espacio disponible para publicidad"}
      </div>
    </section>
  );
}

// ---------- Home page ----------

const BLOCK_RENDERERS = {
  main: MainBlock,
  featured: FeaturedBlock,
  today: TodayCarousel,
  latest: LatestBlock,
  grid: GridBlock,
  category: CategoryBlock,
  most_read: MostReadBlock,
  videos: VideosBlock,
  recommended: RecommendedBlock,
  breaking: BreakingHomeBanner,
  live: LiveBlock,
  ad_slot: AdSlotPlaceholderBlock,
};

export default function HomePage() {
  const [data, setData] = useState({ blocks: [], loading: true, error: null });
  const [seo, setSeo] = useState(null);

  const loadLayout = React.useCallback(async (attempt = 0) => {
    try {
      const r = await api.homepageLayout();
      const blocks = r.blocks || [];
      setData({ blocks, loading: false, error: null });
      // Persist last-known-good snapshot
      if (blocks.length > 0) {
        try { localStorage.setItem("sp_home_layout_cache", JSON.stringify({ ts: Date.now(), blocks })); } catch { /* ignore */ }
      }
    } catch (e) {
      // Retry up to 3 times with backoff
      if (attempt < 3) {
        setTimeout(() => loadLayout(attempt + 1), 800 * (attempt + 1));
        return;
      }
      // Final fallback: use cached snapshot if available (< 30 min old)
      try {
        const raw = localStorage.getItem("sp_home_layout_cache");
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.blocks?.length && Date.now() - (cached.ts || 0) < 30 * 60 * 1000) {
            setData({ blocks: cached.blocks, loading: false, error: null });
            return;
          }
        }
      } catch (_e) { /* ignore */ }
      setData({ blocks: [], loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    setData({ blocks: [], loading: true, error: null });
    loadLayout(0);
    api.seoSite().then(setSeo).catch(() => {});
  }, [loadLayout]);

  useSEO(seo ? {
    title: seo.default_title,
    description: seo.default_description,
    canonical: seo.public_site_url || (typeof window !== "undefined" ? window.location.origin : ""),
    image: seo.default_image,
    og: {
      type: "website",
      url: seo.public_site_url || (typeof window !== "undefined" ? window.location.origin : ""),
      title: seo.default_title,
      description: seo.default_description,
      image: seo.default_image,
      site_name: seo.site_name,
      locale: seo.locale,
    },
    twitter: {
      card: "summary_large_image",
      site: seo.twitter_handle || "",
      title: seo.default_title,
      description: seo.default_description,
      image: seo.default_image,
    },
    facebookAppId: seo.facebook_app_id,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "NewsMediaOrganization",
      "name": seo.site_name,
      "url": seo.public_site_url,
      "logo": seo.default_image,
      "description": seo.default_description,
      "sameAs": [],
    },
  } : {});

  return (
    <div data-testid={TID.homePage} className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8 py-8">
      {data.loading ? (
        <div className="flex gap-8 items-start">
          <div className="flex-1 min-w-0">
            <HeroSkeleton />
            <div className="mt-10">
              <GridSkeleton count={8} columns={4} />
            </div>
          </div>
          <Sidebar />
        </div>
      ) : data.blocks.length === 0 ? (
        <div className="text-center py-16 text-textgray text-sm">
          {data.error ? (
            <>
              <p className="mb-3">No pudimos cargar las noticias en este momento.</p>
              <button
                onClick={() => loadLayout(0)}
                data-testid="home-retry-load"
                className="px-4 py-2 border border-softblue text-nightblue hover:bg-lightgray transition-colors text-xs uppercase tracking-widest"
              >
                Reintentar
              </button>
            </>
          ) : (
            "Todavía no hay bloques de portada configurados. Volvé pronto."
          )}
        </div>
      ) : (
        (() => {
          // "Destacados a ancho completo": el primer bloque tipo main o featured
          // se renderiza a ancho completo, arriba del sidebar. Los demás bloques
          // permanecen dentro de la columna principal junto al sidebar.
          const blocks = data.blocks;
          const featuredIdx = blocks.findIndex((b) => b.kind === "main" || b.kind === "featured");
          const featuredBlock = featuredIdx >= 0 ? blocks[featuredIdx] : null;
          const restBlocks = featuredBlock
            ? blocks.filter((_, i) => i !== featuredIdx)
            : blocks;
          const FeaturedRenderer = featuredBlock ? BLOCK_RENDERERS[featuredBlock.kind] : null;
          let heroAdInserted = false;
          return (
            <>
              {featuredBlock && FeaturedRenderer && (
                <div data-testid="home-featured-fullwidth" className="mb-6">
                  <FeaturedRenderer block={featuredBlock} />
                  <AdSlot slot="below-hero" className="mt-2 mb-4 flex justify-center" />
                  <MobileAdsStrip className="mt-4" />
                </div>
              )}
              <div className="flex gap-8 items-start">
                <div className="flex-1 min-w-0">
                  {restBlocks.map((block, idx) => {
                    const Renderer = BLOCK_RENDERERS[block.kind];
                    if (!Renderer) return null;
                    // If no featured block existed at all, keep the classic hero ad insertion.
                    const shouldInsertHeroAd = !featuredBlock && !heroAdInserted && block.kind === "main";
                    if (shouldInsertHeroAd) heroAdInserted = true;
                    return (
                      <React.Fragment key={block.id}>
                        <Renderer block={block} />
                        {shouldInsertHeroAd && (
                          <AdSlot slot="below-hero" className="my-6 flex justify-center" />
                        )}
                        {idx > 0 && idx < restBlocks.length - 1 && idx % 3 === 0 && (
                          <AdSlot slot="between-blocks" className="my-6 flex justify-center" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                <Sidebar />
              </div>
              <MobileAdsStrip className="mt-8" />
            </>
          );
        })()
      )}
    </div>
  );
}
